import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchMerchantsByIds } from 'core/actions/ocapi';
import { useTriggerSectionIsViewedGaEvent } from 'core/hooks/useTriggerSectionIsViewedGaEvent';
import { isElementVisibleInViewport } from 'core/utils/helpers/isElementVisibleInViewport';

import throttle from 'lodash/throttle';
import isEmpty from 'lodash/isEmpty';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';
import List from 'core/modules/Partials/List/List';
import MerchantTileListItem from 'core/modules/MerchantTileList/components/MerchantTileListItem/MerchantTileListItem';

import { selectFavoritesIsProcessed, selectFavoriteMerchantsFavorited } from 'core/selectors/favorites';
import { selectSiteUrl } from 'core/selectors/app';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';

import { selectFavoriteStoresConfig, selectFavoriteStoresMerchants } from './selectors';

import {
  triggerFavoriteStoresIsViewed, triggerFavoriteStoresManageFavoritesTileIsViewed, clearFavoriteStoresList,
} from './actions';

import './FavoriteStores.scss';

function FavoriteStores() {
  const dispatch = useDispatch();
  const siteUrl = useSelector(selectSiteUrl);
  const favoritesIsProcessed = useSelector(selectFavoritesIsProcessed);
  const favoriteMerchantsFavoritedIds = useSelector(selectFavoriteMerchantsFavorited);

  const favoriteStoresElement = useRef();
  const wasManageFavoritesTileVisibleInViewportBefore = useRef(false);

  const config = useSelector(selectFavoriteStoresConfig);
  const merchants = useSelector(selectFavoriteStoresMerchants);
  const {
    getSecTitle, manageFavoritesLanguage, rebateOptions, tagHelper, carousel,
  } = config;

  const isMobileMQ = useSelector(selectIsMobileMQ);
  const secTitle = getSecTitle(isMobileMQ);

  const renderManageFavoritesTile = () => (
    <a
      className="mn_manageFavoritesTileWrap"
      href={`${siteUrl}/favorites____.htm`}
      title="Opens favorites page"
    >
      <div className="mn_manageFavoritesTile">
        <p>{manageFavoritesLanguage}<span className="mn_dot">.</span><span className="mn_arrow" /></p>
      </div>
    </a>
  );

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!favoritesIsProcessed) { return; }

    if (favoriteMerchantsFavoritedIds.length) {
      const { api: { params, options } = {} } = config;
      dispatch(fetchMerchantsByIds(favoriteMerchantsFavoritedIds, params, options));
    } else {
      dispatch(clearFavoriteStoresList());
    }
  }, [favoritesIsProcessed, favoriteMerchantsFavoritedIds]);

  /* GA event, which is triggered when user scroll PAGE to the 'Favorite Stores' section */
  const isSectionShown = !isEmpty(merchants);
  const dispatchTriggerSectionIsViewed = () => dispatch(triggerFavoriteStoresIsViewed(merchants));
  useTriggerSectionIsViewedGaEvent(favoriteStoresElement, dispatchTriggerSectionIsViewed, isSectionShown);

  /* GA event, which is triggered when user scroll CAROUSEL to the 'Manage your favorite' tile */
  const throttledHandleManageFavoritesTileGaEvents = throttle(handleManageFavoritesTileGaEvents, 500);

  function handleManageFavoritesTileGaEvents() {
    const isSectionVisibleInViewport = isElementVisibleInViewport(favoriteStoresElement.current, 0);
    const isManageFavoritesTileShown =
      favoriteStoresElement.current?.querySelector('.mn_manageFavoritesTileWrap[data-slide-in-view]');

    if (isSectionVisibleInViewport && isManageFavoritesTileShown) {
      dispatch(triggerFavoriteStoresManageFavoritesTileIsViewed());
      wasManageFavoritesTileVisibleInViewportBefore.current = true;
      window.removeEventListener('scroll', throttledHandleManageFavoritesTileGaEvents);
    }
  }

  useEffect(() => {
    const cleanup = () => window.removeEventListener('scroll', throttledHandleManageFavoritesTileGaEvents);

    if (wasManageFavoritesTileVisibleInViewportBefore.current) { return cleanup; }
    handleManageFavoritesTileGaEvents();
    // handle Chase tagging implementation
    favoriteStoresElement.current && tagHelper(favoriteStoresElement.current);
    window.addEventListener('scroll', throttledHandleManageFavoritesTileGaEvents);
    return cleanup;
  }, [merchants]);

  const onSettle = () => {
    handleManageFavoritesTileGaEvents();
  };

  if (isEmpty(merchants)) {
    return null;
  }

  return (
    <div className="mn_favoriteStores" ref={favoriteStoresElement}>
      <ContentWrap mobile tablet l xl>
        <SectionTitle title={secTitle} />
        <List options={carousel} onSettle={onSettle} shouldUseCarousel>
          {merchants.map((merchant) => (
            <MerchantTileListItem key={merchant.id} merchant={merchant} rebateOptions={rebateOptions} />
          ))}
          {renderManageFavoritesTile()}
        </List>
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><FavoriteStores {...props} /></ComponentErrorBoundary>;
